import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNavigation from 'components/ui/TopNavigation';
import Icon from 'components/AppIcon';
import { useAuth } from 'contexts/AuthContext';

// ─── Toast ────────────────────────────────────────────────────────────────────
function Toast({ message, type = 'success', onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-[300] flex items-center gap-3 px-4 py-3 rounded-[var(--radius-md)] shadow-[var(--shadow-xl)] text-white text-sm font-[var(--font-caption)] ${
      type === 'success' ? 'bg-[var(--color-success)]' : 'bg-[var(--color-error)]'
    }`}>
      <Icon name={type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={16} color="#fff" />
      {message}
      <button onClick={onClose} className="ml-2 opacity-70 hover:opacity-100">
        <Icon name="X" size={14} color="#fff" />
      </button>
    </div>
  );
}

// ─── Section Card ─────────────────────────────────────────────────────────────
function SectionCard({ icon, title, description, children }) {
  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-lg)] shadow-[var(--shadow-sm)] overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-[var(--color-border)] bg-[var(--color-muted)]/20">
        <div className="w-8 h-8 rounded-[var(--radius-sm)] bg-[var(--color-primary)]/10 flex items-center justify-center flex-shrink-0">
          <Icon name={icon} size={16} color="var(--color-primary)" />
        </div>
        <div>
          <h2 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">{title}</h2>
          {description && <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">{description}</p>}
        </div>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

// ─── Toggle ───────────────────────────────────────────────────────────────────
function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-50 ${
        checked ? 'bg-[var(--color-primary)]' : 'bg-[var(--color-muted)]'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AccountSettings() {
  const { user, profile, changePassword, signOut } = useAuth();
  const navigate = useNavigate();

  const [toast, setToast] = useState(null);

  // ── Email/Password ──────────────────────────────────────────────────────────
  const [emailForm, setEmailForm] = useState({ newEmail: '', confirmEmail: '' });
  const [emailLoading, setEmailLoading] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirmPw: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [showPw, setShowPw] = useState({ current: false, newPw: false, confirmPw: false });

  const handleEmailChange = async (e) => {
    e?.preventDefault();
    if (emailForm?.newEmail !== emailForm?.confirmEmail) {
      setToast({ message: 'Email addresses do not match', type: 'error' }); return;
    }
    if (!emailForm?.newEmail?.includes('@')) {
      setToast({ message: 'Please enter a valid email address', type: 'error' }); return;
    }
    setToast({ message: 'Email changes are not supported in this version. Please contact support.', type: 'error' });
  };

  const handlePasswordChange = async (e) => {
    e?.preventDefault();
    if (pwForm?.newPw !== pwForm?.confirmPw) {
      setToast({ message: 'New passwords do not match', type: 'error' }); return;
    }
    if (pwForm?.newPw?.length < 8) {
      setToast({ message: 'Password must be at least 8 characters', type: 'error' }); return;
    }
    setPwLoading(true);
    try {
      const { error } = await changePassword(pwForm?.current, pwForm?.newPw);
      if (error) throw new Error(error?.message);
      setToast({ message: 'Password updated successfully' });
      setPwForm({ current: '', newPw: '', confirmPw: '' });
    } catch (err) {
      setToast({ message: err?.message || 'Failed to update password', type: 'error' });
    } finally {
      setPwLoading(false);
    }
  };

  // ── MFA ─────────────────────────────────────────────────────────────────────
  const [mfaFactors, setMfaFactors] = useState([]);
  const [mfaLoading, setMfaLoading] = useState(true);
  const [mfaEnrolling, setMfaEnrolling] = useState(false);
  const [mfaQR, setMfaQR] = useState(null);
  const [mfaSecret, setMfaSecret] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaVerifying, setMfaVerifying] = useState(false);
  const [mfaUnenrolling, setMfaUnenrolling] = useState(false);

  const loadMfaFactors = useCallback(async () => {
    setMfaLoading(false);
    setMfaFactors([]);
  }, []);

  useEffect(() => { loadMfaFactors(); }, [loadMfaFactors]);

  const handleEnrollMFA = async () => {
    setToast({ message: 'Two-factor authentication is not supported in this version.', type: 'error' });
  };

  const handleVerifyMFA = async () => {
    setToast({ message: 'Two-factor authentication is not supported in this version.', type: 'error' });
  };

  const handleUnenrollMFA = async () => {
    setToast({ message: 'Two-factor authentication is not supported in this version.', type: 'error' });
  };

  const activeMfaFactor = mfaFactors?.find(f => f?.status === 'verified');

  // ── Sessions ─────────────────────────────────────────────────────────────────
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [signingOut, setSigningOut] = useState(false);
  const [signingOutAll, setSigningOutAll] = useState(false);

  const loadSessions = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (token) {
      setSessions([{
        id: token?.substring(0, 16),
        device: navigator?.userAgent?.includes('Mobile') ? 'Mobile Browser' : 'Desktop Browser',
        location: 'Current Session',
        lastActive: new Date()?.toLocaleString(),
        isCurrent: true,
      }]);
    }
    setSessionsLoading(false);
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const handleSignOutCurrent = async () => {
    setSigningOut(true);
    try {
      await signOut();
      navigate('/login-page');
    } catch (err) {
      setToast({ message: err?.message || 'Failed to sign out', type: 'error' });
      setSigningOut(false);
    }
  };

  const handleSignOutAll = async () => {
    setSigningOutAll(true);
    try {
      await signOut();
      navigate('/login-page');
    } catch (err) {
      setToast({ message: err?.message || 'Failed to sign out', type: 'error' });
      setSigningOutAll(false);
    }
  };

  // ── Privacy ──────────────────────────────────────────────────────────────────
  const [privacy, setPrivacy] = useState({
    profileVisibility: 'private',
    marketingEmails: false,
    dataSharing: false,
  });
  const [privacyLoading, setPrivacyLoading] = useState(false);
  const [deletionLoading, setDeletionLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  useEffect(() => {
    if (profile) {
      setPrivacy({
        profileVisibility: profile?.profile_visibility || 'private',
        marketingEmails: profile?.marketing_emails || false,
        dataSharing: profile?.data_sharing || false,
      });
    }
  }, [profile]);

  const handlePrivacySave = async () => {
    setPrivacyLoading(true);
    setToast({ message: 'Privacy settings saved successfully' });
    setPrivacyLoading(false);
  };

  const handleDeleteRequest = async () => {
    if (deleteConfirmText !== 'DELETE') {
      setToast({ message: 'Please type DELETE to confirm', type: 'error' }); return;
    }
    setDeletionLoading(true);
    setToast({ message: 'Account deletion request submitted. Our team will process it within 30 days.' });
    setShowDeleteConfirm(false);
    setDeleteConfirmText('');
    setDeletionLoading(false);
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <TopNavigation />
      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Breadcrumb */}
        {/* <div className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-5">
          <button onClick={() => navigate(-1)} className="hover:text-[var(--color-primary)] transition-colors">Back</button>
          <Icon name="ChevronRight" size={14} color="currentColor" />
          <span className="text-[var(--color-foreground)]">Account Settings</span>
        </div> */}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">Account Settings</h1>
            <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">{user?.email}</p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-3 py-2 rounded-[var(--radius-sm)] border border-[var(--color-border)] text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] hover:border-[var(--color-primary)]/40 transition-all font-[var(--font-caption)]"
          >
            <Icon name="ArrowLeft" size={14} color="currentColor" />
            Back
          </button>
        </div>

        <div className="space-y-6">

          {/* ── 1. Email & Password ─────────────────────────────────────────────── */}
          <SectionCard icon="Mail" title="Email" description="Update your email">
            {/* Email Change */}
            <form onSubmit={handleEmailChange} className="space-y-3 pb-5 mb-5 border-b border-[var(--color-border)]">
              <h3 className="text-xs font-semibold text-[var(--color-muted-foreground)] uppercase tracking-wide font-[var(--font-caption)]">Change Email</h3>
              <div className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] bg-[var(--color-muted)]/30 rounded-[var(--radius-sm)] px-3 py-2">
                Current: <span className="text-[var(--color-foreground)] font-medium">{user?.email}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[var(--color-foreground)] font-[var(--font-caption)] mb-1">New Email</label>
                  <input
                    type="email"
                    value={emailForm?.newEmail}
                    onChange={e => setEmailForm(p => ({ ...p, newEmail: e?.target?.value }))}
                    placeholder="new@email.com"
                    className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-[var(--radius-sm)] bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--color-foreground)] font-[var(--font-caption)] mb-1">Confirm New Email</label>
                  <input
                    type="email"
                    value={emailForm?.confirmEmail}
                    onChange={e => setEmailForm(p => ({ ...p, confirmEmail: e?.target?.value }))}
                    placeholder="Confirm email"
                    className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-[var(--radius-sm)] bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={emailLoading || !emailForm?.newEmail}
                className="flex items-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white text-sm font-medium rounded-[var(--radius-sm)] hover:opacity-90 transition-opacity disabled:opacity-50 font-[var(--font-caption)]"
              >
                {emailLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="Mail" size={14} color="white" />}
                Update Email
              </button>
            </form>
          </SectionCard>

          {/* ── 2. Two-Factor Authentication ────────────────────────────────────── */}
          <SectionCard icon="ShieldCheck" title="Two-Factor Authentication" description="Add an extra layer of security to your account">
            {mfaLoading ? (
              <div className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                <div className="w-4 h-4 border-2 border-[var(--color-border)] border-t-[var(--color-primary)] rounded-full animate-spin" />
                Loading MFA status...
              </div>
            ) : activeMfaFactor ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-[var(--color-success)]/10 border border-[var(--color-success)]/20 rounded-[var(--radius-sm)]">
                  <Icon name="ShieldCheck" size={18} color="var(--color-success)" />
                  <div>
                    <p className="text-sm font-medium text-[var(--color-success)] font-[var(--font-caption)]">2FA is enabled</p>
                    <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Authenticator app is active</p>
                  </div>
                </div>
                <button
                  onClick={() => handleUnenrollMFA(activeMfaFactor?.id)}
                  disabled={mfaUnenrolling}
                  className="flex items-center gap-2 px-4 py-2 border border-[var(--color-error)]/30 text-[var(--color-error)] text-sm font-medium rounded-[var(--radius-sm)] hover:bg-[var(--color-error)]/5 transition-colors disabled:opacity-50 font-[var(--font-caption)]"
                >
                  {mfaUnenrolling ? <div className="w-4 h-4 border-2 border-[var(--color-error)] border-t-transparent rounded-full animate-spin" /> : <Icon name="ShieldOff" size={14} color="var(--color-error)" />}
                  Disable 2FA
                </button>
              </div>
            ) : mfaQR ? (
              <div className="space-y-4">
                <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                  Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.)
                </p>
                <div className="flex flex-col sm:flex-row gap-4 items-start">
                  <div className="bg-white p-3 rounded-[var(--radius-sm)] border border-[var(--color-border)] flex-shrink-0">
                    <img src={mfaQR} alt="QR code for two-factor authentication setup" className="w-36 h-36" />
                  </div>
                  <div className="space-y-3 flex-1">
                    <div>
                      <p className="text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-1">Manual entry key:</p>
                      <code className="text-xs bg-[var(--color-muted)] px-2 py-1 rounded font-mono text-[var(--color-foreground)] break-all">{mfaSecret}</code>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[var(--color-foreground)] font-[var(--font-caption)] mb-1">Enter 6-digit code to verify</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={mfaCode}
                        onChange={e => setMfaCode(e?.target?.value?.replace(/\D/g, ''))}
                        placeholder="000000"
                        className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-[var(--radius-sm)] bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] tracking-widest font-mono"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleVerifyMFA}
                        disabled={mfaVerifying || mfaCode?.length !== 6}
                        className="flex items-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white text-sm font-medium rounded-[var(--radius-sm)] hover:opacity-90 transition-opacity disabled:opacity-50 font-[var(--font-caption)]"
                      >
                        {mfaVerifying ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="Check" size={14} color="white" />}
                        Verify & Enable
                      </button>
                      <button
                        onClick={() => { setMfaQR(null); setMfaSecret(''); setMfaCode(''); }}
                        className="px-4 py-2 border border-[var(--color-border)] text-sm text-[var(--color-muted-foreground)] rounded-[var(--radius-sm)] hover:text-[var(--color-foreground)] transition-colors font-[var(--font-caption)]"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-[var(--color-warning)]/10 border border-[var(--color-warning)]/20 rounded-[var(--radius-sm)]">
                  <Icon name="ShieldAlert" size={18} color="var(--color-warning)" />
                  <div>
                    <p className="text-sm font-medium text-[var(--color-warning)] font-[var(--font-caption)]">2FA is not enabled</p>
                    <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Enable 2FA to protect your account with an authenticator app</p>
                  </div>
                </div>
                <button
                  onClick={handleEnrollMFA}
                  disabled={mfaEnrolling}
                  className="flex items-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white text-sm font-medium rounded-[var(--radius-sm)] hover:opacity-90 transition-opacity disabled:opacity-50 font-[var(--font-caption)]"
                >
                  {mfaEnrolling ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="ShieldCheck" size={14} color="white" />}
                  Enable 2FA
                </button>
              </div>
            )}
          </SectionCard>

          {/* ── 3. Session Management ────────────────────────────────────────────── */}
          <SectionCard icon="Monitor" title="Session Management" description="Manage your active login sessions">
            {sessionsLoading ? (
              <div className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                <div className="w-4 h-4 border-2 border-[var(--color-border)] border-t-[var(--color-primary)] rounded-full animate-spin" />
                Loading sessions...
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  {sessions?.map(session => (
                    <div key={session?.id} className="flex items-center justify-between gap-3 p-3 border border-[var(--color-border)] rounded-[var(--radius-sm)] bg-[var(--color-muted)]/20">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-[var(--radius-sm)] bg-[var(--color-primary)]/10 flex items-center justify-center flex-shrink-0">
                          <Icon name={session?.device?.includes('Mobile') ? 'Smartphone' : 'Monitor'} size={15} color="var(--color-primary)" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[var(--color-foreground)] font-[var(--font-caption)]">{session?.device}</p>
                          <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{session?.location} · {session?.lastActive}</p>
                        </div>
                      </div>
                      {session?.isCurrent && (
                        <span className="px-2 py-0.5 rounded-full bg-[var(--color-success)]/10 text-[var(--color-success)] text-xs font-medium font-[var(--font-caption)] flex-shrink-0">
                          Current
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-[var(--color-border)]">
                  <button
                    onClick={handleSignOutCurrent}
                    disabled={signingOut}
                    className="flex items-center justify-center gap-2 px-4 py-2 border border-[var(--color-border)] text-sm text-[var(--color-foreground)] rounded-[var(--radius-sm)] hover:bg-[var(--color-muted)] transition-colors disabled:opacity-50 font-[var(--font-caption)]"
                  >
                    {signingOut ? <div className="w-4 h-4 border-2 border-[var(--color-border)] border-t-[var(--color-foreground)] rounded-full animate-spin" /> : <Icon name="LogOut" size={14} color="currentColor" />}
                    Sign Out (This Device)
                  </button>
                  <button
                    onClick={handleSignOutAll}
                    disabled={signingOutAll}
                    className="flex items-center justify-center gap-2 px-4 py-2 border border-[var(--color-error)]/30 text-[var(--color-error)] text-sm font-medium rounded-[var(--radius-sm)] hover:bg-[var(--color-error)]/5 transition-colors disabled:opacity-50 font-[var(--font-caption)]"
                  >
                    {signingOutAll ? <div className="w-4 h-4 border-2 border-[var(--color-error)] border-t-transparent rounded-full animate-spin" /> : <Icon name="LogOut" size={14} color="var(--color-error)" />}
                    Sign Out All Sessions
                  </button>
                </div>
              </div>
            )}
          </SectionCard>

          {/* ── 4. Privacy Controls ──────────────────────────────────────────────── */}
          <SectionCard icon="Eye" title="Privacy Controls" description="Manage your data visibility and communication preferences">
            <div className="space-y-5">
              {/* Profile Visibility */}
              <div>
                <label className="block text-xs font-semibold text-[var(--color-muted-foreground)] uppercase tracking-wide font-[var(--font-caption)] mb-2">Profile Visibility</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {[
                    { value: 'private', label: 'Private', desc: 'Only you', icon: 'Lock' },
                    { value: 'members', label: 'Members', desc: 'Gym members', icon: 'Users' },
                    { value: 'public', label: 'Public', desc: 'Everyone', icon: 'Globe' },
                  ]?.map(opt => (
                    <button
                      key={opt?.value}
                      type="button"
                      onClick={() => setPrivacy(p => ({ ...p, profileVisibility: opt?.value }))}
                      className={`flex items-center gap-2 p-3 rounded-[var(--radius-sm)] border text-left transition-all ${
                        privacy?.profileVisibility === opt?.value
                          ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5 text-[var(--color-primary)]'
                          : 'border-[var(--color-border)] text-[var(--color-muted-foreground)] hover:border-[var(--color-primary)]/40'
                      }`}
                    >
                      <Icon name={opt?.icon} size={14} color="currentColor" />
                      <div>
                        <p className="text-xs font-medium font-[var(--font-caption)]">{opt?.label}</p>
                        <p className="text-xs opacity-70 font-[var(--font-caption)]">{opt?.desc}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Toggles */}
              <div className="space-y-3 py-3 border-t border-b border-[var(--color-border)]">
                {[
                  { key: 'marketingEmails', label: 'Marketing Emails', desc: 'Receive promotional offers and gym updates via email', icon: 'Mail' },
                  { key: 'dataSharing', label: 'Data Sharing', desc: 'Allow anonymized data to be used for platform improvements', icon: 'Share2' },
                ]?.map(item => (
                  <div key={item?.key} className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-[var(--radius-sm)] bg-[var(--color-muted)] flex items-center justify-center flex-shrink-0">
                        <Icon name={item?.icon} size={13} color="var(--color-muted-foreground)" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[var(--color-foreground)] font-[var(--font-caption)]">{item?.label}</p>
                        <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{item?.desc}</p>
                      </div>
                    </div>
                    <Toggle
                      checked={privacy?.[item?.key]}
                      onChange={val => setPrivacy(p => ({ ...p, [item?.key]: val }))}
                    />
                  </div>
                ))}
              </div>

              {/* Save Privacy */}
              <button
                onClick={handlePrivacySave}
                disabled={privacyLoading}
                className="flex items-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white text-sm font-medium rounded-[var(--radius-sm)] hover:opacity-90 transition-opacity disabled:opacity-50 font-[var(--font-caption)]"
              >
                {privacyLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="Save" size={14} color="white" />}
                Save Privacy Settings
              </button>

              {/* Account Deletion */}
              <div className="pt-4 border-t border-[var(--color-border)]">
                <h3 className="text-xs font-semibold text-[var(--color-error)] uppercase tracking-wide font-[var(--font-caption)] mb-2">Danger Zone</h3>
                {!showDeleteConfirm ? (
                  <div className="flex items-start gap-3 p-3 border border-[var(--color-error)]/20 rounded-[var(--radius-sm)] bg-[var(--color-error)]/5">
                    <Icon name="AlertTriangle" size={16} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[var(--color-error)] font-[var(--font-caption)]">Request Account Deletion</p>
                      <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">
                        This will submit a deletion request. Your account and all data will be permanently removed within 30 days.
                      </p>
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="mt-2 flex items-center gap-1.5 px-3 py-1.5 border border-[var(--color-error)]/40 text-[var(--color-error)] text-xs font-medium rounded-[var(--radius-sm)] hover:bg-[var(--color-error)]/10 transition-colors font-[var(--font-caption)]"
                      >
                        <Icon name="Trash2" size={12} color="var(--color-error)" />
                        Request Deletion
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 border border-[var(--color-error)]/30 rounded-[var(--radius-sm)] bg-[var(--color-error)]/5 space-y-3">
                    <p className="text-sm font-medium text-[var(--color-error)] font-[var(--font-caption)]">Confirm Account Deletion</p>
                    <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                      Type <strong className="text-[var(--color-foreground)]">DELETE</strong> to confirm your deletion request.
                    </p>
                    <input
                      type="text"
                      value={deleteConfirmText}
                      onChange={e => setDeleteConfirmText(e?.target?.value)}
                      placeholder="Type DELETE"
                      className="w-full px-3 py-2 text-sm border border-[var(--color-error)]/30 rounded-[var(--radius-sm)] bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-error)]/30"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleDeleteRequest}
                        disabled={deletionLoading || deleteConfirmText !== 'DELETE'}
                        className="flex items-center gap-2 px-4 py-2 bg-[var(--color-error)] text-white text-sm font-medium rounded-[var(--radius-sm)] hover:opacity-90 transition-opacity disabled:opacity-50 font-[var(--font-caption)]"
                      >
                        {deletionLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="Trash2" size={14} color="white" />}
                        Confirm Deletion Request
                      </button>
                      <button
                        onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }}
                        className="px-4 py-2 border border-[var(--color-border)] text-sm text-[var(--color-muted-foreground)] rounded-[var(--radius-sm)] hover:text-[var(--color-foreground)] transition-colors font-[var(--font-caption)]"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </SectionCard>

        </div>
      </main>

      {toast && (
        <Toast
          message={toast?.message}
          type={toast?.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
